import type { CartPanelItem } from '../../hooks/useCartPanelItems'
import type { TripListPrice, TripListRow, TripListRowType } from './tripListTypes'

const LE_PRICED_ROW_TYPES: ReadonlySet<TripListRowType> = new Set(['accommodation', 'experience', 'flight'])

export function getTripListRowTripItemId(row: TripListRow): number {
  return row.kind === 'item' ? row.item.id : row.option.itemId
}

export function isTripListRowMatchingCartPanelItem(row: TripListRow, item: CartPanelItem): boolean {
  const tripItemId = getTripListRowTripItemId(row)
  if (row.kind === 'transport') {
    return item.productType === 'flight' && item.pairedTripItemIds.includes(tripItemId)
  }
  return item.tripItemId === tripItemId
}

function isCartPanelPriceUsable(rowPrice: TripListPrice, item: CartPanelItem): boolean {
  if (item.pricing.price === undefined) return false
  return !item.pricing.loading || rowPrice.price === undefined
}

function isExactCartPanelPrice(item: CartPanelItem): boolean {
  return item.source === 'cart' && item.cartStatus === 'active'
}

function getCartPanelPriceCurrency(rowPrice: TripListPrice, currentCurrency: string): string | undefined {
  return currentCurrency || rowPrice.currency
}

function getCartPanelRowPrice(
  rowPrice: TripListPrice,
  item: CartPanelItem,
  currentCurrency: string,
): TripListPrice {
  const displayType = isExactCartPanelPrice(item) ? 'total' : 'from'

  return {
    price: item.pricing.price,
    memberPrice: item.pricing.memberPrice,
    currency: getCartPanelPriceCurrency(rowPrice, currentCurrency),
    saleUnit: displayType === 'total' ? undefined : rowPrice.saleUnit,
    displayType,
  }
}

function shouldUseCartPanelPricing(row: TripListRow): boolean {
  return LE_PRICED_ROW_TYPES.has(row.rowType)
}

export function applyCartPanelPricesToTripListRows(
  rows: Array<TripListRow>,
  cartPanelItems: Array<CartPanelItem>,
  currentCurrency: string,
): Array<TripListRow> {
  // A grouped multi-city cart item is one item whose `pairedTripItemIds` cover
  // every leg, so it matches each leg row and carries the whole-itinerary total.
  // Apply that total to the first matching row only — pricing later legs with the
  // same total would inflate the footer sum (a 3-leg A$461 itinerary footing as
  // A$1,383). Tracked by item identity since the grouped item has no cartItemId.
  const pricedCartItems = new Set<CartPanelItem>()

  return rows.map(row => {
    if (!shouldUseCartPanelPricing(row)) return row

    const cartPanelItem = cartPanelItems.find(item => isTripListRowMatchingCartPanelItem(row, item))
    if (!cartPanelItem || !isCartPanelPriceUsable(row.price, cartPanelItem)) return row

    if (pricedCartItems.has(cartPanelItem)) {
      return { ...row, price: { ...row.price, price: undefined, memberPrice: undefined } }
    }
    pricedCartItems.add(cartPanelItem)

    return {
      ...row,
      price: getCartPanelRowPrice(row.price, cartPanelItem, currentCurrency),
    }
  })
}
